<template>
  <div class="has-text-left">
    <h5 class="text-gray-900 text-base font-semibold">{{$t("enter_bank_details")}}</h5>

    <div class="flex gap-5 mt-7 items-start flex-col md:flex-row">
      <div class="w-full md:w-1/2">
        <label
          for="bank_name"
          class="text-base text-gray-900"
        >{{$t('bank_name').tTitle()}}</label>
         <input
          v-validate="'required'"
          :class="{'input': true, 'is-danger': errors.has('bank_name') }"
          name="bank_name"
          type="text"
          :data-vv-as="translations.text.bank_name"
          :placeholder="translations.text.bank_name"
          v-model="bank_name"
          class="mt-2 bg-white"
        />
        <span
            v-show="errors.has('bank_name')"
            class="text-red text-xs pt-0.5 rtl:text-right"
          >{{ errors.first('bank_name') }}</span>
      </div>
      <div class="w-full md:w-1/2">
        <label
          for="bank_account_holder_name"
          class="text-base text-gray-900"
        >{{$t('bank_account_holder_name').tTitle()}}</label>
        <input
          v-validate="'required'"
          :class="{'input': true, 'is-danger': errors.has('bank_account_holder_name') }"
          name="bank_account_holder_name"
          type="text"
          :data-vv-as="translations.text.bank_account_holder_name"
          :placeholder="$t('bank_holder_name')"
          v-model="bank_account_holder_name"
          class="mt-2 bg-white"
        />
        <span
          v-show="errors.has('bank_account_holder_name')"
          class="text-red text-xs pt-0.5 rtl:text-right"
        >{{ errors.first('bank_account_holder_name') }}</span>
      </div>
    </div>

    <div class="mt-5">
      <div class="flex gap-5 mt-7 items-start flex-col md:flex-row">
        <div class="w-full md:w-1/2">
          <label
            for="bank_account_number"
            class="text-base text-gray-900"
          >{{$t('bank_account_number').tTitle()}}</label>
          <input
            v-validate="'required'"
            :class="{'input': true, 'is-danger': errors.has('bank_account_number') }"
            name="bank_account_number"
            type="text"
            :data-vv-as="translations.text.bank_account_number"
            :placeholder="'00000000'"
            v-model="bank_account_number"
            class="mt-2 bg-white"
          />
          <span
              v-show="errors.has('bank_account_number')"
              class="text-red text-xs pt-0.5 rtl:text-right"
            >{{ errors.first('bank_account_number') }}</span>
        </div>
        <div class="w-full md:w-1/2">
          <label
            for="routing_number"
            class="text-base text-gray-900"
          >{{$t('routing_number').tTitle()}}</label>
          <input
            v-validate="'required'"
            :class="{'input': true, 'is-danger': errors.has('routing_number') }"
            name="routing_number"
            type="text"
            :data-vv-as="translations.text.routing_number"
            :placeholder="'000000000'"
            v-model="routing_number"
            class="mt-2 bg-white"
          />
          <span
              v-show="errors.has('routing_number')"
              class="text-red text-xs pt-0.5 rtl:text-right"
            >{{ errors.first('routing_number') }}</span>
        </div>
      </div>
    </div>

    <h5 class="text-gray-900 text-base font-semibold mt-5 md:mt-8">{{$t("bank_address")}}</h5>

    <div class="mt-2">
      <div class="flex gap-5 items-start flex-col md:flex-row">
        <div class="w-full md:w-[173px] md:flex-shrink-0">
          <div class="relative">
            <select
              v-validate="'required'"
              name="bank_address_country"
              :data-vv-as="translations.text.bank_address_country"
              v-model="bank_address.country"
              @mousedown="selectMousePress"
              @click="selectClickHandle"
              @blur="selectBlurHandle"
              @keydown="selectKeyHandle"
              class="rounded-md border-none"
              :class="[{ 'select-placeholder': bank_address.country === '' }]"
            >
              <option value="" disabled hidden>{{ $t('country').tTitle() }}</option>
              <option v-for="(option, key) in countries" :key="key" :value="key">
                {{ option.name }}
              </option>
            </select>
            <img
              :class="{ 'rotate-180': openSelect === 'bank_address_country' }"
              class="absolute top-[18px] h-[13px] ltr:right-3 rtl:left-3 transform transition-transform duration-200 pointer-events-none"
              width="13px"
              :src="errors.has('bank_address_country') ? require('../../../assets/img/dropdown-arrow-red.svg') : require('../../../assets/img/icons/upload/arrow-down.svg')"
              alt="arrow"
            />
          </div>
          <span
            v-show="errors.has('bank_address_country')"
            class="text-red text-xs pt-0.5 rtl:text-right"
          >{{ errors.first('bank_address_country') }}</span>
        </div>
        <div class="w-full md:w-auto flex-grow">
          <input
            v-validate="'required'"
            :class="{'input': true, 'is-danger': errors.has('bank_address_street') }"
            name="bank_address_street"
            type="text"
            :data-vv-as="translations.text.bank_address_street"
            :placeholder="$t('street')"
            v-model="bank_address.street"
            class="bg-white"
          />
          <span
              v-show="errors.has('bank_address_street')"
              class="text-red text-xs pt-0.5 rtl:text-right"
            >{{ errors.first('bank_address_street') }}</span>
        </div>
      </div>
    </div>

    <div class="mt-5">
      <div class="flex gap-5 items-start flex-col md:flex-row">
        <div class="w-full md:w-[173px] md:flex-shrink-0">
          <input
            v-validate="'required'"
            :class="{'input': true, 'is-danger': errors.has('bank_address_postalCode') }"
            name="bank_address_postalCode"
            type="text"
            :data-vv-as="translations.text.bank_address_postalCode"
            :placeholder="$t('post_zip_code').tTitle()"
            v-model="bank_address.postalCode"
            class="bg-white"
          />
          <span
            v-show="errors.has('bank_address_postalCode')"
            class="text-red text-xs pt-0.5 rtl:text-right"
          >{{ errors.first('bank_address_postalCode') }}</span>
        </div>
        <div class="w-full md:w-auto flex-grow flex gap-5 flex-col md:flex-row">
          <div class="w-full md:w-1/2">
              <input
                v-validate="'required'"
                :class="{'input': true, 'is-danger': errors.has('bank_address_city') }"
                name="bank_address_city"
                type="text"
                :data-vv-as="translations.text.bank_address_city"
                :placeholder="$t('city')"
                v-model="bank_address.city"
                class="bg-white"
              />
              <span
                  v-show="errors.has('bank_address_city')"
                  class="text-red text-xs pt-0.5 rtl:text-right"
                >{{ errors.first('bank_address_city') }}</span>
          </div>
          <div class="w-full md:w-1/2">
              <input
                v-validate="'required'"
                :class="{'input': true, 'is-danger': errors.has('bank_address_state') }"
                name="bank_address_state"
                type="text"
                :data-vv-as="translations.text.bank_address_state"
                :placeholder="$t('state_province')"
                v-model="bank_address.state"
                class="bg-white"
              />
              <span
                  v-show="errors.has('bank_address_state')"
                  class="text-red text-xs pt-0.5 rtl:text-right"
              >{{ errors.first('bank_address_state') }}</span>
          </div>
        </div>
      </div>
    </div>

    <h5 class="text-gray-900 text-base font-semibold mt-12 md:mt-8">{{$t("bank_enter_withdrawal_amount")}}</h5>

    <div class="flex gap-5 mt-7 items-start flex-col md:flex-row">
      <!-- <div class="w-full md:w-auto">
        <label
          for="currency"
          class="text-base text-gray-900"
        >{{$t('currency').tTitle()}}</label>
        <div class="mt-2">
          <div class="bg-white py-3 w-full md:w-44 text-gray-900 text-base font-semibold ltr:pl-5 rtl:pr-5 rounded-md">USD</div>
        </div>
      </div> -->
      <div class="w-full md:w-1/2">
        <label
          for="payment_method"
          class="text-base text-gray-900"
        >{{$t('payment_method').tTitle()}}</label>
        <div class="mt-2">
          <div class="relative">
            <select
              v-validate="'required'"
              name="payment_method"
              v-model="payment_method"
              :data-vv-as="translations.text.payment_method"
              @mousedown="selectMousePress"
              @click="selectClickHandle"
              @blur="selectBlurHandle"
              @keydown="selectKeyHandle"
              class="rounded-md border-none"
              :class="[{ 'select-placeholder': payment_method === '' }]"
            >
              <option value="" disabled hidden>{{ $t('select_method').tTitle() }}</option>
              <option>Fedwire</option>
              <option>ACH</option>
            </select>
            <img
              :class="{ 'rotate-180': openSelect === 'payment_method' }"
              class="absolute top-[18px] h-[13px] ltr:right-3 rtl:left-3 transform transition-transform duration-200 pointer-events-none"
              width="13px"
              :src="errors.has('payment_method') ? require('../../../assets/img/dropdown-arrow-red.svg') : require('../../../assets/img/icons/upload/arrow-down.svg')"
              alt="arrow"
            />
          </div>
          <span
            v-show="errors.has('payment_method')"
            class="text-red text-xs pt-0.5 rtl:text-right"
          >{{ errors.first('payment_method') }}</span>
        </div>
      </div>
      <div class="w-full md:w-1/2">
        <label
          for="amount"
          class="text-base text-gray-900"
        >{{$t('amount').tTitle()}}</label>
        <input
          v-validate="'required|currency|min_value:500|max_value:50000'"
          :class="{'input': true, 'is-danger': errors.has('amount') }"
          name="amount"
          type="text"
          :data-vv-as="translations.text.amount"
          :placeholder="0"
          v-model="amount"
          class="mt-2 bg-white"
        />
        <span
          v-show="errors.has('amount')"
          class="text-red text-xs pt-0.5 rtl:text-right"
        >{{ errors.first('amount') }}</span>
      </div>
    </div>

    <div class="mt-5 mb-10">
      <p class="text-gray-900 text-base">{{ $t('popular_withdrawals') }}</p>
      <div class="flex mt-2 gap-1 md:gap-2 flex-wrap xl:flex-nowrap">
        <div v-for="item in popularWithdrawals" :key="item" @click="
            selectedPopularWithdrawal = item
            amount = item
          "
          class="w-[100px] xl:w-1/6 flex-grow md:flex-grow-0 shrink-0 xl:shrink py-3 text-gray-900 text-base font-semibold rounded-md text-center bg-white border border-solid cursor-pointer focus:border-0 focus:outline-none focus:ring-0"
          :class="[selectedPopularWithdrawal == item ? 'border-green' : 'border-white']">
          {{ walletSymbol + item }}
        </div>
      </div>
    </div>
    <Button mode="dark" @click="withdraw" customClasses="!w-52">
      {{$t("submit").tTitle()}}
    </Button>
    <p class="pt-4 text-gray-900 text-sm mb-10">{{ $t('bankwire_withdrawal_note') }}</p>
  </div>
</template>

<script>
import Button from '@/components/reusableComponents/Button.vue'
import config from '../../../config'
import { mapGetters } from 'vuex'

export default {
  components: { Button },
  data() {
    return {
      translations: {
        text: {
          bank_name: this.$t('bank_name'),
          bank_account_holder_name: this.$t('bank_account_holder_name'),
          bank_account_number: this.$t('bank_account_number'),
          routing_number: this.$t('routing_number'),
          bank_address_country: this.$t('country').tTitle(),
          bank_address_street: this.$t('street'),
          bank_address_postalCode: this.$t('post_zip_code'),
          bank_address_city: this.$t('city'),
          bank_address_state: this.$t('state_province'),
          payment_method: this.$t('payment_method'),
        },
      },
      currencySymbols: config.currencies,
      countries: config.countries,
      selectedPopularWithdrawal: '',
      popularWithdrawals: ['500', '1000', '2000', '3000','4000','5000'],
      bank_name: '',
      bank_account_holder_name: '',
      bank_account_number: '',
      routing_number: '',
      bank_address: {
        country: '',
        street: '',
        postalCode: '',
        city: '',
        state: '',
      },
      payment_method: '',
      amount: '',
      openSelect: ''
    }
  },
  watch: {
    // watcher for the i18n language change
    '$i18n.locale'() {
      this.$validator.reset()
    },
  },
  computed: {
    ...mapGetters({
      User: 'user/all',
      Language: 'language/code',
      wallet:'user/wallet'
    }),
    walletSymbol() {
      return '$';
      // return this.currencySymbols[this.wallet.currency].symbol;
    }
  },
  mounted() {},
  methods: {
    selectClickHandle(event) {
      const component = event.currentTarget
      const rect = component.getBoundingClientRect()
      const { clientX, clientY } = event
      const isInComponent = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
      if (!isInComponent) {
        this.openSelect = ''
      }
    },
    selectBlurHandle() {
      this.openSelect = ''
    },
    selectKeyHandle(event) {
      if (event.key === 'Enter' || event.keyCode === 13 || event.key === ' ' || event.keyCode === 32) {
        this.openSelect = event.currentTarget.name
      }
    },
    selectMousePress(event) {
      const name = event.currentTarget.name
      this.openSelect = this.openSelect === name ? '' : name
    },
    withdraw() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$bus.$emit('RequestWithdraw', '/finance/withdraw', {
            userID: this.$store.state.user.info.ID,
            // bank_name: this.bank_name,
            // bank_account_holder_name: this.bank_account_holder_name,
            // routing_number: this.routing_number,
            // bank_address: this.bank_address.street + ', ' + this.bank_address.postalCode + ', ' + this.bank_address.city + ', ' + this.bank_address.state + ', ' + this.bank_address.country,
            // payment_method: this.payment_method,
            amount: String(this.amount),
            psp: 'bankwire',
            extraInfo: JSON.stringify({ bank_name: this.bank_name, bank_account_holder_name: this.bank_account_holder_name, bank_account_number: this.bank_account_number, routing_number: this.routing_number, bank_address_street: this.bank_address.street, bank_address_postal_code: this.bank_address.postalCode, bank_address_city: this.bank_address.city, bank_address_state: this.bank_address.state, bank_address_country: this.bank_address.country, payment_method: this.payment_method }),
          })
        }
      })
    },
  },
}
</script>
